import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Paper
} from '@mui/material';
import axios from 'axios';
import BASE_URL from '../../config';

const CustomerChat = ({ uid, serial }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const chatContainerRef = useRef(null);
  const token = localStorage.getItem('jwt_token');

  useEffect(() => {
    if (!uid) return;

    const fetchMessages = async () => {
      setLoadingMessages(true);
      try {
        const response = await axios.post(`${BASE_URL}/api/get_cust_messages_admin/`, { uid }, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        setMessages(response.data);
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [uid]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${BASE_URL}/api/create_text_admin/`, {
        sender: 'ADMIN', recipient: uid, message: newMessage,
      }, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });

      setNewMessage('');
      const response = await axios.post(`${BASE_URL}/api/get_cust_messages_admin/`, { uid }, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
  component={Paper}
  p={2}
  mt={2}
  maxWidth="600px"
  width="100%"
  mx="auto"
  borderRadius={2}
  sx={{
    background: 'rgba(35, 47, 62, 0.6)',
    border: '1px solid rgba(255, 153, 0, 0.2)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  }}
>

      <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>Chat with Device: {serial || uid}</Typography>
      <Box
        ref={chatContainerRef}
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          p: 1,
          background: 'rgba(15, 20, 25, 0.8)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 153, 0, 0.2)',
          mb: 2
        }}
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          setIsAtBottom(scrollHeight - scrollTop - clientHeight < 100);
        }}
      >
        {loadingMessages ? <CircularProgress /> :
          messages.map((msg, index) => {
            const isDevice = msg.sender === uid;
  return (
    <Box
      key={index}
      mb={1}
      display="flex"
      flexDirection="column"
      alignItems={isDevice ? 'flex-start' : 'flex-end'}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.75rem',
          alignSelf: isDevice ? 'flex-start' : 'flex-end',
          color: '#aab7c4'
        }}
      >
        {isDevice ? serial || 'Device' : 'You'}
      </Typography>

      <Box
        component="div"
        sx={{
          background: isDevice
            ? 'rgba(35, 47, 62, 0.8)'
            : 'linear-gradient(135deg, #ff9900 0%, #ff6600 100%)',
          color: 'white',
          p: 1.5,
          borderRadius: 2,
          maxWidth: '70%',
          border: isDevice ? '1px solid rgba(255, 153, 0, 0.2)' : 'none',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: 'white' }}
        >
          {msg.message}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            display: 'block',
            textAlign: 'right',
            color: 'rgba(255, 255, 255, 0.8)'
          }}
        >
          {formatTime(msg.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
          })}
      </Box>

      {!isAtBottom && (
        <Box display="flex" justifyContent="center" mb={1}>
          <Button
            size="small"
            onClick={() => {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }}
            sx={{
              background: 'rgba(255, 153, 0, 0.2)',
              color: '#ff9900',
              border: '1px solid rgba(255, 153, 0, 0.4)',
              '&:hover': {
                background: 'rgba(255, 153, 0, 0.3)',
              }
            }}
          >
            Scroll to Latest
          </Button>
        </Box>
      )}

      <Box display="flex" gap={1}>
        <TextField
          fullWidth
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          sx={{
            '& .MuiInputBase-root': {
              background: 'rgba(35, 47, 62, 0.8)',
              color: '#ffffff',
              border: '1px solid rgba(255, 153, 0, 0.3)',
              borderRadius: '6px',
            },
            '& .MuiInputBase-input': {
              color: '#ffffff',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(170, 183, 196, 0.6)',
              opacity: 1,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-root:hover': {
              borderColor: 'rgba(255, 153, 0, 0.5)',
            },
            '& .MuiInputBase-root.Mui-focused': {
              borderColor: 'rgba(255, 153, 0, 0.6)',
              boxShadow: '0 0 8px rgba(255, 153, 0, 0.3)',
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
          sx={{
            background: 'linear-gradient(135deg, #ff9900 0%, #ff6600 100%)',
            color: 'white',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(255, 153, 0, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #ffaa00 0%, #ff7700 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(255, 153, 0, 0.4)',
            },
            '&:disabled': {
              background: 'rgba(35, 47, 62, 0.5)',
              color: 'rgba(170, 183, 196, 0.5)',
            },
          }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerChat;
